import axios from "axios";
import { toast } from "sonner";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

/**
 * Converte QUALQUER erro de axios em uma string legível pro usuário.
 *
 * Trata especificamente o caso do Pydantic v2 / FastAPI 422 onde `detail` vem
 * como ARRAY de objetos `[{type, loc, msg, input, url}, ...]`. Se esses objetos
 * chegassem sem tratamento até `toast.error(...)` ou fossem renderizados como
 * children React, causariam **React error #31** ("Objects are not valid as a
 * React child"), derrubando a tela inteira via ErrorBoundary — bug reportado
 * fev/2026 na produção do mobile PWA após confirmar entrega.
 */
export const humanErrorMsg = (err, fallback = "Erro inesperado") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  // Array de erros Pydantic — coleta msgs mais úteis
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => {
      if (typeof d === "string") return d;
      const field = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
      const msg = d?.msg || d?.message || "";
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    return msgs.length ? msgs.join(" · ") : fallback;
  }
  // Objeto único { msg, ... }
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return fallback;
};

// withCredentials: envia cookie httpOnly `access_token` automaticamente.
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((c) => {
  const eid = localStorage.getItem("current_empresa_id");
  if (eid) c.headers["X-Empresa-Id"] = eid;
  return c;
});

// Interceptor global de erro (fev/2026):
// 1. Normaliza `detail` para STRING antes de qualquer caller ler `err.response.data.detail`.
//    Isso previne React #31 quando componentes passam o detail direto pra toast/render.
// 2. Mostra toast "Acesso negado" automaticamente em qualquer 403 (matrix bloqueou ação).
//    Skip: endpoints marcados com `_silent403: true` no config da request.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Normalização defensiva: sobrescreve o detail com uma string legível.
    // Preserva o objeto original em `_rawDetail` caso alguém precise inspecionar depois.
    if (err?.response?.data && typeof err.response.data === "object") {
      const raw = err.response.data.detail;
      if (raw !== undefined && typeof raw !== "string") {
        err.response.data._rawDetail = raw;
        err.response.data.detail = humanErrorMsg(err, "Falha na validação");
      }
    }
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;  // agora sempre string após normalização
    const silent = err?.config?._silent403;
    if (status === 403 && !silent) {
      toast.error(detail || "Acesso negado — sem permissão para esta ação", {
        duration: 5000,
      });
    }
    // 402 Payment Required — licença bloqueada (fev/2026). Dispara evento global
    // para o LicenseGate renderizar a tela de bloqueio mesmo em navegação por URL
    // direta que faça chamada de API antes do gate ter lido /license/status.
    if (status === 402) {
      const body = err?.response?.data || {};
      window.dispatchEvent(new CustomEvent("license-blocked", { detail: {
        reason: body.reason,
        empresa_id: body.empresa_id,
        empresa_nome: body.empresa_nome,
        expires_at: body.expires_at,
        observacao: body.observacao,
      }}));
    }
    return Promise.reject(err);
  }
);

export const fmtBRL = (n) =>
  (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Formata ISO datetime UTC em hora local (BRT)
export const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso.slice(11, 19); }
};
// Formata ISO datetime em data local dd/mm/aaaa
export const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR"); }
  catch { return iso.slice(0, 10); }
};
// Formata ISO datetime completo (para relatórios)
export const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("pt-BR", { hour12: false }); }
  catch { return iso.slice(0, 19).replace("T", " "); }
};

export const parseBRL = (s) => {
  if (typeof s === "number") return s;
  if (!s) return 0;
  const cleaned = String(s).replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
};

export const calcTroco = (valor, valorRecebido, trocoPara) => {
  const v = Number(valor) || 0;
  const r = Number(valorRecebido) || 0;
  const p = Number(trocoPara) || 0;
  // Preferência: se trocoPara informado, troco = trocoPara - valor
  if (p > 0) return Math.max(0, p - v);
  if (r > 0) return Math.max(0, r - v);
  return 0;
};

export const sugerirTrocoPara = (valor) => {
  const v = Number(valor) || 0;
  if (v <= 0) return 0;
  if (v < 50) return 50;
  if (v < 100) return 100;
  return Math.ceil(v / 50) * 50;
};

export const STATUS = {
  aguardando: "Aguardando",
  aguardando_conferencia: "Aguardando Conferência",
  cancelada: "Cancelada",
  em_transito: "Em Trânsito",
  concluida: "Concluída",
  remarcar: "Remarcar",
};
